import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge, AthletePhoto } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime, MEMBERSHIP_CLS } from "@/lib/format";

const Info = ({ label, value, mono }) => (
  <div><div className="eyebrow">{label}</div><div className={`mt-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div></div>
);

export function PersonalData({ a }) {
  return (
    <div className="card-surface mb-8 p-6" data-testid="my-personal-data">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="font-display text-base font-semibold">I miei dati personali</div>
        <Badge cls={MEMBERSHIP_CLS[a.membership_status]} testid="my-membership">Tesseramento {a.membership_status} · fino al {fmtDate(a.membership_expiry)}</Badge>
      </div>
      <div className="flex flex-col gap-6 sm:flex-row">
        <AthletePhoto athleteId={a.id} hasPhoto={a.has_photo} name={`${a.first_name} ${a.last_name}`} className="h-28 w-24" rounded="rounded-xl" />
        <div className="grid flex-1 grid-cols-2 gap-4 md:grid-cols-4">
          <Info label="Nome e cognome" value={`${a.first_name} ${a.last_name}`} />
          <Info label="Codice fiscale" value={a.tax_code} mono />
          <Info label="Nascita" value={`${fmtDate(a.birth_date)}${a.birth_place ? ` · ${a.birth_place}` : ""}`} />
          <Info label="Tessera MSP" value={a.membership_number} mono />
          <Info label="ASD/SSD" value={a.club} />
          <Info label="Email" value={a.email} />
          <Info label="Telefono" value={a.phone} />
        </div>
      </div>
      <div className="mt-4 text-xs text-slate-500">Per correggere i tuoi dati rivolgiti al tuo settore di riferimento.</div>
    </div>
  );
}

export function RenewalDialog({ lic, onOpenChange, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setNote(""), [lic]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/licenses/${lic.id}/renewal`, { note });
      toast.success("Richiesta di rinnovo inviata al tuo settore");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!lic} onOpenChange={onOpenChange}>
      <DialogContent data-testid="renewal-dialog">
        <DialogHeader><DialogTitle>Richiedi il rinnovo</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Licenza {lic?.license_number} · {lic?.discipline_name}. La richiesta arriva al responsabile del tuo settore, che la inoltrerà alla sede nazionale.</p>
        <Textarea placeholder="Nota per il settore (facoltativa)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} data-testid="renewal-note-input" />
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="renewal-submit-btn">Invia richiesta</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const R_CLS = { inviata: "bg-amber-50 text-amber-800 border-amber-200", inoltrata: "bg-emerald-50 text-emerald-800 border-emerald-200", respinta: "bg-red-50 text-red-800 border-red-200" };

export function RenewalList({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="mt-10" data-testid="my-renewals">
      <div className="mb-3 font-display text-lg font-semibold">Le mie richieste di rinnovo</div>
      <div className="card-surface divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
            <div>
              <div className="font-mono font-semibold">{r.license_number}</div>
              <div className="text-xs text-slate-500">Inviata il {fmtDateTime(r.created_at)}{r.response ? ` · ${r.response}` : ""}</div>
            </div>
            <Badge cls={R_CLS[r.status]}>{r.status_label}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}
